import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";

import {
	defaultTQC2011_R01HeaderDto,
	TQC2011_R01DetailDto,
	TQC2011_R01HeaderDto,
} from "./Dto_TQC2011_R01";

export interface TQC2011_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	itemCd: string;
	itemNm: string;
	productEmp: string;
	productEmpNm: string;
}

export const defaultTQC2011_R01Filter: TQC2011_R01Filter = {
	compCd: "1000",
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	workplaceCd: "",
	itemCd: "",
	itemNm: "",
	productEmp: "",
	productEmpNm: "",
};

export interface TQC2011_R01Info {
	inspectType: string;
	inspectDt: Date | undefined;
	inspectorEmp: string;
	inspectorEmpNm: string;
	approvorEmpno: string;
	approvorEmpNm: string;
	inspectRemark: string;
}

export const defaultTQC2011_R01Info: TQC2011_R01Info = {
	inspectType: "",
	inspectDt: undefined,
	inspectorEmp: "",
	inspectorEmpNm: "",
	approvorEmpno: "",
	approvorEmpNm: "",
	inspectRemark: "",
};

interface Store_TQC2011_R01 {
	TQC2011_R01Filter: TQC2011_R01Filter;
	TQC2011_R01PastFilter: TQC2011_R01Filter;
	setTQC2011_R01Filter: (filter: Partial<TQC2011_R01Filter>) => void;
	setTQC2011_R01PastFilter: (filter: Partial<TQC2011_R01Filter>) => void;
	TQC2011_R01Info: TQC2011_R01Info;
	setTQC2011_R01Info: (info: Partial<TQC2011_R01Info>) => void;
	TQC2011_R01List: TQC2011_R01HeaderDto[];
	setTQC2011_R01List: (list: TQC2011_R01HeaderDto[]) => void;
	selectedTQC2011_R01: TQC2011_R01HeaderDto | undefined;
	setSelectedTQC2011_R01: (item: TQC2011_R01HeaderDto | undefined) => void;
	initSelectedTQC2011_R01: () => void;
	TQC2011_R01DetailList: TQC2011_R01DetailDto[];
	setTQC2011_R01DetailList: (list: TQC2011_R01DetailDto[]) => void;
	updateTQC2011_R01DetailList: (valueIndex: number, data: TQC2011_R01DetailDto) => void;
	originalTQC2011_R01DetailList: TQC2011_R01DetailDto[];
	setOriginalTQC2011_R01DetailList: (list: TQC2011_R01DetailDto[]) => void;
	allCount: number;
	setAllCount: (allCount: number) => void;
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
	initData: () => void;
}

const withHeaderIndex = (list: TQC2011_R01HeaderDto[]) =>
	list.map((item, index) => ({
		...defaultTQC2011_R01HeaderDto,
		...item,
		valueIndex: index + 1,
	}));

const withDetailIndex = (list: TQC2011_R01DetailDto[]) =>
	list.map((item, index) => ({ ...item, valueIndex: index + 1, isModified: false }));

export const useTQC2011_R01Store = create<Store_TQC2011_R01>((set) => ({
	TQC2011_R01Filter: defaultTQC2011_R01Filter,
	TQC2011_R01PastFilter: defaultTQC2011_R01Filter,
	setTQC2011_R01Filter: (filter) =>
		set((state) => ({ TQC2011_R01Filter: { ...state.TQC2011_R01Filter, ...filter } })),
	setTQC2011_R01PastFilter: (filter) =>
		set((state) => ({
			TQC2011_R01PastFilter: { ...state.TQC2011_R01PastFilter, ...filter },
		})),
	TQC2011_R01Info: defaultTQC2011_R01Info,
	setTQC2011_R01Info: (info) =>
		set((state) => ({ TQC2011_R01Info: { ...state.TQC2011_R01Info, ...info } })),
	TQC2011_R01List: [],
	setTQC2011_R01List: (list) => set({ TQC2011_R01List: withHeaderIndex(list) }),
	selectedTQC2011_R01: undefined,
	setSelectedTQC2011_R01: (item) => set({ selectedTQC2011_R01: item }),
	initSelectedTQC2011_R01: () => set({ selectedTQC2011_R01: undefined }),
	TQC2011_R01DetailList: [],
	setTQC2011_R01DetailList: (list) =>
		set({ TQC2011_R01DetailList: withDetailIndex(list) }),
	updateTQC2011_R01DetailList: (valueIndex, data) =>
		set((state) => ({
			TQC2011_R01DetailList: state.TQC2011_R01DetailList.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),
	originalTQC2011_R01DetailList: [],
	setOriginalTQC2011_R01DetailList: (list) =>
		set({ originalTQC2011_R01DetailList: withDetailIndex(list) }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),
	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),
	initData: () =>
		set({
			TQC2011_R01Filter: defaultTQC2011_R01Filter,
			TQC2011_R01PastFilter: defaultTQC2011_R01Filter,
			TQC2011_R01Info: defaultTQC2011_R01Info,
			TQC2011_R01List: [],
			selectedTQC2011_R01: undefined,
			TQC2011_R01DetailList: [],
			originalTQC2011_R01DetailList: [],
			allCount: 0,
			scrollTop: null,
			detailScrollTop: null,
		}),
}));

export default useTQC2011_R01Store;
